import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, Check, IndianRupee, MessageSquare, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_META = {
  planning: { bg: "#B5793F", label: "Planning" },
  in_progress: { bg: "#19B5DE", label: "In progress" },
  on_hold: { bg: "#8A7E6B", label: "On hold" },
  completed: { bg: "#23C795", label: "Completed" },
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function ProjectsDashboard({ items }) {
  const active = items.filter((p) => p.status !== "completed");
  const completed = items.length - active.length;
  const pipeline = active.reduce((s, p) => s + (p.price || 0), 0);
  const received = items.reduce((s, p) => s + (p.paid || 0), 0);
  const outstanding = items.reduce((s, p) => s + (p.balance || 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const pending = items
    .flatMap((p) => p.milestones.filter((m) => !m.done).map((m) => ({ ...m, project: p.title })))
    .sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999"))
    .slice(0, 5);

  const stats = [
    { label: "Active projects", value: active.length, color: "#262016" },
    { label: "Completed", value: completed, color: "#23C795" },
    { label: "Pipeline value", value: inr(pipeline), color: "#262016" },
    { label: "Received", value: inr(received), color: "#23C795" },
    { label: "Outstanding", value: inr(outstanding), color: outstanding > 0 ? "#B5793F" : "#23C795" },
  ];

  return (
    <section className="mb-12" data-testid="projects-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="rp-foam bg-white/55 p-5" data-testid={`pstat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="rp-display text-2xl font-medium leading-none" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      {pending.length > 0 && (
        <div className="rp-foam bg-white/55 p-5 mt-6" data-testid="upcoming-milestones">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium mb-3">Next milestones</p>
          <div className="space-y-2.5">
            {pending.map((m) => {
              const overdue = m.due && m.due < today;
              return (
                <div key={m.id} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: overdue ? "#DC2626" : "#B5793F" }} />
                  <span className="text-sm text-[#262016] flex-1 min-w-0 truncate">{m.title}</span>
                  <span className="text-xs text-[#8A7E6B] truncate max-w-40">{m.project}</span>
                  <span className="text-[11px] shrink-0 font-medium" style={{ color: overdue ? "#DC2626" : "#8A7E6B" }}>
                    {m.due ? (overdue ? `Overdue · ${m.due}` : m.due) : "No date"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8A7E6B] mb-1">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "w-full rounded-xl border border-[#26201620] bg-white px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]";

function ProjectCard({ p, onChanged }) {
  const navigate = useNavigate();
  const [edit, setEdit] = useState({ title: p.title, price: p.price, notes: p.notes || "", description: p.description || "" });
  const [pay, setPay] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), mode: "UPI", note: "" });
  const [ms, setMs] = useState({ title: "", due: "" });

  const call = async (method, path, body) => {
    const r = await fetch(`${API}/api/admin/projects/${p.id}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Failed"); return false; }
    onChanged();
    return true;
  };

  const saveDetails = async () => {
    if (await call("PATCH", "", { title: edit.title, price: Number(edit.price) || 0, notes: edit.notes, description: edit.description }))
      toast.success("Project saved");
  };

  const addPayment = async () => {
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Enter a valid amount");
    if (await call("POST", "/payments", { ...pay, amount: Number(pay.amount) })) {
      toast.success("Payment recorded — client emailed a receipt");
      setPay({ amount: "", date: new Date().toISOString().slice(0, 10), mode: "UPI", note: "" });
    }
  };

  const addMilestone = async () => {
    if (!ms.title.trim()) return toast.error("Milestone needs a title");
    if (await call("POST", "/milestones", ms)) { toast.success("Milestone added"); setMs({ title: "", due: "" }); }
  };

  const st = STATUS_META[p.status] || STATUS_META.planning;
  return (
    <article className="bg-white rounded-2xl p-6 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`project-${p.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 text-[#F6F1E5]" style={{ background: st.bg }}>{st.label}</span>
        <span className="rp-display text-lg text-[#262016]">{p.title}</span>
        <span className="text-xs text-[#8A7E6B]">{p.client_name} · {p.client_email}</span>
        <select value={p.status} onChange={(e) => call("PATCH", "", { status: e.target.value })} data-testid={`project-status-${p.id}`}
                className="ml-auto text-xs rounded-full border border-[#26201625] bg-white px-3 py-1.5 text-[#262016] outline-none">
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-5">
        <div className="rounded-xl bg-white/70 border border-[#26201610] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B]">Project value</p>
          <p className="rp-display text-2xl text-[#262016] mt-1" data-testid={`project-price-${p.id}`}>{inr(p.price)}</p>
        </div>
        <div className="rounded-xl bg-white/70 border border-[#26201610] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B]">Received</p>
          <p className="rp-display text-2xl mt-1" style={{ color: "#23C795" }} data-testid={`project-paid-${p.id}`}>{inr(p.paid)}</p>
        </div>
        <div className="rounded-xl bg-white/70 border border-[#26201610] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B]">Balance</p>
          <p className="rp-display text-2xl mt-1" style={{ color: p.balance > 0 ? "#B5793F" : "#23C795" }} data-testid={`project-balance-${p.id}`}>{inr(p.balance)}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* left: details */}
        <div className="space-y-6">
          <Field label="Title"><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inputCls} data-testid={`project-title-input-${p.id}`} /></Field>
          <Field label="Pricing (INR)"><input type="number" min="0" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className={inputCls} data-testid={`project-price-input-${p.id}`} /></Field>
          <Field label="Scope / description"><textarea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inputCls} /></Field>
          <Field label="Private notes (client never sees these)"><textarea rows={2} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={inputCls} data-testid={`project-notes-input-${p.id}`} /></Field>
          <button onClick={saveDetails} data-testid={`project-save-${p.id}`}
                  className="rounded-full px-6 py-2.5 text-sm text-[#F6F1E5]" style={{ background: "#262016" }}>Save details</button>
        </div>

        {/* right: milestones + payments */}
        <div className="space-y-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B] mb-2">Milestones — {p.progress}% done</p>
            <div className="space-y-3">
              {p.milestones.map((m) => (
                <div key={m.id} className="flex items-center gap-2.5 rounded-xl bg-white/70 border border-[#26201610] px-3 py-2" data-testid={`milestone-${m.id}`}>
                  <button onClick={() => call("PATCH", `/milestones/${m.id}`, { done: !m.done })} data-testid={`milestone-toggle-${m.id}`}
                          className="w-5 h-5 rounded-md border flex items-center justify-center shrink-0"
                          style={{ background: m.done ? "#23C795" : "transparent", borderColor: m.done ? "#23C795" : "#26201640" }}>
                    {m.done && <Check size={12} className="text-white" />}
                  </button>
                  <span className={`flex-1 text-sm ${m.done ? "line-through text-[#8A7E6B]" : "text-[#262016]"}`}>{m.title}</span>
                  {m.due && <span className="text-[11px] text-[#8A7E6B]">{m.due}</span>}
                  <button onClick={() => call("DELETE", `/milestones/${m.id}`)} className="text-[#8A7E6B] hover:text-red-700"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input placeholder="New milestone" value={ms.title} onChange={(e) => setMs({ ...ms, title: e.target.value })} className={inputCls} data-testid={`milestone-title-input-${p.id}`} />
              <input type="date" value={ms.due} onChange={(e) => setMs({ ...ms, due: e.target.value })} className={`${inputCls} w-40`} />
              <button onClick={addMilestone} data-testid={`milestone-add-${p.id}`} className="rounded-full px-4 text-sm text-[#F6F1E5] shrink-0" style={{ background: "#262016" }}><Plus size={14} /></button>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#8A7E6B] mb-2">Payments received</p>
            <div className="space-y-3">
              {p.payments.map((x) => (
                <div key={x.id} className="flex items-center gap-3 rounded-xl bg-white/70 border border-[#26201610] px-3 py-2" data-testid={`payment-${x.id}`}>
                  <IndianRupee size={13} style={{ color: "#23C795" }} />
                  <span className="text-sm font-semibold text-[#262016]">{inr(x.amount)}</span>
                  <span className="text-xs text-[#8A7E6B]">{x.date} · {x.mode}{x.note ? ` · ${x.note}` : ""}</span>
                  <button onClick={() => call("DELETE", `/payments/${x.id}`)} className="ml-auto text-[#8A7E6B] hover:text-red-700"><Trash2 size={13} /></button>
                </div>
              ))}
              {p.payments.length === 0 && <p className="text-xs text-[#8A7E6B]">Nothing recorded yet.</p>}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <input type="number" min="1" placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={`${inputCls} w-28`} data-testid={`payment-amount-input-${p.id}`} />
              <input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className={`${inputCls} w-40`} />
              <select value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })} className={`${inputCls} w-28`}>
                {["UPI", "Bank transfer", "Cash", "Card", "Other"].map((m) => <option key={m}>{m}</option>)}
              </select>
              <input placeholder="Note (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} className={`${inputCls} flex-1 min-w-32`} />
              <button onClick={addPayment} data-testid={`payment-add-${p.id}`} className="rounded-full px-5 py-2 text-sm text-[#F6F1E5]" style={{ background: "#262016" }}>Record</button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#26201612] flex items-center gap-4">
        <button onClick={() => navigate("/tribe/admin")} className="inline-flex items-center gap-1.5 text-xs text-[#B5793F] hover:underline" data-testid={`project-conversation-${p.id}`}>
          <MessageSquare size={13} /> Original conversation in Services
        </button>
        <button onClick={async () => { if (window.confirm("Delete this project?") && await call("DELETE", "")) toast.success("Project deleted"); }}
                className="ml-auto inline-flex items-center gap-1 text-xs text-[#8A7E6B] hover:text-red-700" data-testid={`project-delete-${p.id}`}>
          <Trash2 size={13} /> Delete project
        </button>
      </div>
    </article>
  );
}

function NewProjectForm({ onDone, onCancel }) {
  const [f, setF] = useState({ title: "", client_name: "", client_email: "", description: "", status: "planning", price: "" });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!f.title.trim() || !f.client_name.trim()) return toast.error("Title and client name are required");
    setBusy(true);
    const r = await fetch(`${API}/api/admin/projects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...f, price: Number(f.price) || 0 }),
    });
    setBusy(false);
    if (r.ok) { toast.success("Project created"); onDone(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Could not create"); }
  };

  return (
    <article className="rp-foam bg-white/55 p-6 mb-8" data-testid="new-project-form">
      <p className="rp-display text-lg text-[#262016] mb-4">New project</p>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Project title *"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="np-title" /></Field>
        <Field label="Pricing (INR)"><input type="number" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inputCls} data-testid="np-price" /></Field>
        <Field label="Client name *"><input value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} className={inputCls} data-testid="np-client-name" /></Field>
        <Field label="Client email (links to their Tribe account if they have one)"><input value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} className={inputCls} data-testid="np-client-email" /></Field>
      </div>
      <div className="mt-3">
        <Field label="Scope / description"><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} data-testid="np-description" /></Field>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="np-status"
                className="text-xs rounded-full border border-[#26201625] bg-white px-3 py-2 text-[#262016] outline-none">
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={create} disabled={busy} data-testid="np-create-btn"
                className="rounded-full px-6 py-2.5 text-sm text-[#F6F1E5] disabled:opacity-50" style={{ background: "#262016" }}>
          {busy ? "Creating…" : "Create project"}
        </button>
        <button onClick={onCancel} className="text-sm text-[#8A7E6B] hover:text-[#262016]" data-testid="np-cancel-btn">Cancel</button>
      </div>
    </article>
  );
}

export default function AdminProjects() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () =>
    fetch(`${API}/api/admin/projects`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setItems)
      .catch(() => setItems(false));

  useEffect(() => { load(); }, []);

  if (items === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#EDE7D9" }}><p className="rp-display rp-italic text-[#5C5042]">One moment…</p></div>;
  if (items === false) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#EDE7D9" }}><p className="text-sm text-[#8A7E6B]">Could not load projects.</p></div>;

  return (
    <div className="min-h-screen" style={{ background: "#EDE7D9" }} data-testid="admin-projects-page">
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 md:py-14">
        <button onClick={() => navigate("/tribe/admin")} className="inline-flex items-center gap-2 mb-8 text-sm font-medium text-[#5C5042] hover:text-[#262016]" data-testid="projects-back-btn">
          <ArrowLeft size={15} /> Back to admin
        </button>
        <div className="flex items-center gap-3 mb-2">
          <FolderKanban size={22} style={{ color: "#B5793F" }} />
          <h1 className="rp-display text-4xl sm:text-5xl font-light text-[#262016]">Projects</h1>
        </div>
        <p className="text-sm text-[#5C5042] mb-10">Won service requests, direct clients and your existing work — pricing, payments and milestones in one place.</p>

        <ProjectsDashboard items={items} />

        {adding ? (
          <NewProjectForm onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
        ) : (
          <button onClick={() => setAdding(true)} data-testid="add-project-btn"
                  className="mb-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5]"
                  style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
            <Plus size={15} /> Add project manually
          </button>
        )}

        {items.length === 0 && (
          <p className="text-sm text-[#8A7E6B]">No projects yet. Convert a closed service request from the admin Services section, or add one manually.</p>
        )}
        <div className="space-y-8">
          {items.map((p) => <ProjectCard key={p.id} p={p} onChanged={load} />)}
        </div>
      </main>
    </div>
  );
}
